import './stackassetmodal.scss';
import { useEffect, useRef } from 'react';

const ASSET_HEIGHT = 78;

const STATUS_LABEL = {
	ONLINE: 'Online',
	AT_RISK: 'At risk',
	IMPACTED: 'Impacted',
	OFFLINE: 'Offline',
};

const STATUS_COLOR = {
	ONLINE: '#62D3AA',
	AT_RISK: '#FFBA5A',
	IMPACTED: '#F46A6A',
	OFFLINE: '#C4CCD5',
};

const alertTextFromKind = (kind) => {
	switch (kind) {
		case 'BLOCKAGE':
			return 'Blockage alert';
		case 'ANOMALY':
			return 'Anomaly alert';
		case 'PUMP':
			return 'Pump alert';
		case 'BLOCKAGE_ANOMALY':
			return 'Blockage & anomaly alert';
		case 'BLOCKAGE_PUMP':
			return 'Blockage & pump alert';
		case 'ANOMALY_PUMP':
			return 'Anomaly & pump alert';
		case 'BLOCKAGE_ANOMALY_PUMP':
			return 'Blockage, anomaly & pump alert';
		default:
			return null;
	}
};

const StackedAssetModal = ({ stackedAssetInfo, onStackedAssetSelected, isDefault, siteAlerts }) => {
	const modalOffset = stackedAssetInfo.assets.length * ASSET_HEIGHT;
	const assets = stackedAssetInfo?.assets ?? [];
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleOutsideClick = (event) => {
			if (!modalRef.current) return;

			if (!modalRef.current.contains(event.target)) {
				onStackedAssetSelected?.(null);
			}
		};

		document.addEventListener('mousedown', handleOutsideClick);
		document.addEventListener('touchstart', handleOutsideClick);

		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
			document.removeEventListener('touchstart', handleOutsideClick);
		};
	}, [onStackedAssetSelected]);

	return (
		<div
			ref={modalRef}
			className="stacked-asset-modal"
			style={{
				top: `${stackedAssetInfo.y - modalOffset}px`,
				left: `calc(${stackedAssetInfo.x}px - (var(--stacked-asset-modal-max-width) / 2))`,
			}}
		>
			<div className="stacked-asset-modal-content">
				<ul className="stacked-asset-list">
					{assets.map((asset) => {
						const name = asset?.name ?? '';
						const type = asset?.type ?? '';
						const hasForecast = asset?.measurementTarget?.hasForecast;
						const statusKey = String(asset?.status || 'ONLINE').toUpperCase();
						const statusText = STATUS_LABEL[statusKey] || 'Online';
						const statusColor = STATUS_COLOR[statusKey] || STATUS_COLOR.ONLINE;

						const ids = asset?.id != null ? [asset.id] : [];
						const alertKind = ids.map((id) => siteAlerts[id]).find(Boolean);
						const alertText = alertTextFromKind(alertKind);
						const sitePredictionAlert = alertText ? ` | ${alertText}` : '';
						const showNoForecast = !isDefault && !hasForecast;
						const safeType = String(type || '')
							.trim()
							.toLowerCase()
							.replace(/\s+/g, '_');
						const iconSrc = `/img/nwlIcons/new/${safeType}.svg`;
						const ICON_NUDGE: Record<string, { x: number; y: number; size?: number }> = {
							conduit: { x: 4, y: -1 }, // smaller glyph because it was not aligned well
						};
						const nudge = ICON_NUDGE[safeType] ?? { x: 0, y: 0 };
						const glyphSize = nudge.size ?? 34;
						return (
							<li
								key={asset.id}
								className="stacked-asset-row"
								onClick={() => onStackedAssetSelected(asset)}
							>
								<div className="stacked-asset-icon" style={{ backgroundColor: statusColor }}>
									<div
										className="stacked-asset-icon-mask"
										style={{
											width: glyphSize,
											height: glyphSize,
											WebkitMaskImage: `url(${iconSrc})`,
											maskImage: `url(${iconSrc})`,
											transform: `translate(${nudge.x}px, ${nudge.y}px)`,
										}}
									/>
								</div>

								<div className="stacked-asset-text">
									<div
										data-testid="tooltip${name}"
										id="tooltip-${name}"
										style={{ color: '#111', minWidth: '180px', padding: '4px 6px' }}
									>
										<div
											style={{
												fontWeight: 600,
												marginBottom: '2px',
												fontSize: '16px',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
										>
											{name}
										</div>
										<div style={{ color: '#6B7280', fontSize: '12px', marginBottom: '2px' }}>
											{type}
										</div>

										{showNoForecast ? (
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '4px',
													fontSize: '13px',
													color: '#077898',
												}}
											>
												<svg
													width="14"
													height="14"
													viewBox="0 0 16 16"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<circle cx="8" cy="8" r="7" stroke="#077898" stroke-width="1.2" />
													<path
														d="M8 4.5C7.65482 4.5 7.375 4.77982 7.375 5.125C7.375 5.47018 7.65482 5.75 8 5.75C8.34518 5.75 8.625 5.47018 8.625 5.125C8.625 4.77982 8.34518 4.5 8 4.5Z"
														fill="#077898"
													/>
													<path
														d="M7.25 7H8.25V11.5H7.25"
														stroke="#077898"
														stroke-width="1.2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<span>No forecast data available</span>
											</div>
										) : (
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px',
													fontSize: '12px',
												}}
											>
												<span
													style={{
														display: 'inline-block',
														width: '12px',
														height: '12px',
														borderRadius: '50%',
														background: statusColor,
														boxShadow: '0 0 0 2px rgba(0,0,0,0.05)',
													}}
												></span>
												<span style={{ fontWeight: '400', fontSize: '14px', color: '#6B7280' }}>
													{statusText}
													{sitePredictionAlert}
												</span>
											</div>
										)}
									</div>
								</div>
							</li>
						);
					})}
				</ul>
			</div>
		</div>
	);
};

export default StackedAssetModal;
